import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import {
  MDSIconKind,
  MDSIconButton,
  MDSIconButtonSize,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { withLineBreaks } from "@/domains/html/withLineBreaks";
import { EventContext } from "@/domains/metrics/context";
import { renderArrayWithAnds } from "@/domains/string/renderArrayWithAnds";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { observer } from "mobx-react-lite";
import { useMemo, useCallback } from "react";

interface CollectionsViewPageHeaderButtonProps {
  collection: CollectionObservable;
}

export const CollectionsViewPageHeaderShareButton = observer<CollectionsViewPageHeaderButtonProps>(
  ({ collection }) => {
    const page = useTabPageStore("collectionsViewPage");

    const { tooltipLabel, iconKind } = useMemo(() => {
      const labels = collection.sharedWithLabels;

      if (collection.hasPendingShare) {
        return {
          tooltipLabel: "Access changes will be applied when you’re online",
          iconKind: MDSIconKind.Spinner,
        };
      }

      if (!labels?.length) {
        if (collection.isShared)
          return {
            tooltipLabel: "Shared with no one",
            iconKind: MDSIconKind.Shared,
          };
        return {
          tooltipLabel: "Private to only me",
          iconKind: MDSIconKind.Lock,
        };
      }

      return {
        tooltipLabel: withLineBreaks(
          `Shared with ${renderArrayWithAnds(
            labels.length >= 5 ? [...labels.slice(0, 3), `${labels.length - 2} others`] : labels
          )}`
        ),
        iconKind: MDSIconKind.Shared,
      };
    }, [collection.sharedWithLabels, collection.isShared, collection.hasPendingShare]);

    const handleClick = useCallback(() => {
      page.shareSheetModal.open({
        id: collection.id,
        entityKind: ShareSheetEntityKind.Collection,
        eventContext: EventContext.CollectionActions,
      });
    }, [page.shareSheetModal, collection.id]);

    const tooltipConfig = useMemo(
      () => ({
        label: tooltipLabel,
        placement: MDSTooltipPlacement.Top,
      }),
      [tooltipLabel]
    );

    return (
      <MDSTooltip config={tooltipConfig}>
        <MDSIconButton
          iconKind={iconKind}
          onClick={handleClick}
          isDisabled={!collection.canWrite}
          size={MDSIconButtonSize.Medium}
        />
      </MDSTooltip>
    );
  }
);
